// FOLLOWER BOOST V8.2 — recherche par cible (@pseudo ou lien public) dans l'en-tête catalogue.
//
// Saisie d'un lien public : réseau détecté d'après le domaine (même registre que le configurateur),
// type de cible déduit prudemment du chemin (profil, publication, vidéo…), puis liste des prestations
// compatibles du catalogue Shopify. Saisie « @pseudo » : le client choisit explicitement le réseau.
// Texte libre : recherche Shopify inchangée.
// Aucune donnée n'est récupérée des réseaux sociaux (ni profil, ni statistiques, ni existence du compte) ;
// la cible est transmise à la fiche (?fb_target=) où elle est revalidée puis confirmée par le client.
import { HOSTS } from './fb-configurator.js';

const MAX = 300;
const box = document.querySelector('[data-fb-tsearch]');
const dataEl = document.querySelector('[data-fb-tsearch-data]');
const form = box?.closest('section, .fb-cathead, body')?.querySelector('form.fb-cathead__search') || document.querySelector('form.fb-cathead__search');
let data = null;
try { data = dataEl ? JSON.parse(dataEl.textContent) : null; } catch { data = null; }

const S = (key, vars = {}) => {
  let s = data?.strings?.[key] || '';
  for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`%${k}%`, v);
  return s;
};
const netName = (p) => data?.names?.[p] || p;
const clean = (v) => String(v || '').replace(/[\u0000-\u001f\u007f]/g, '').trim();

/** Réseau d'un hôte (registre HOSTS du configurateur), sinon null. */
function platformOf(host) {
  const h = host.toLowerCase().replace(/\.$/, '');
  for (const [p, hosts] of Object.entries(HOSTS)) {
    if (hosts.some((d) => h === d || h.endsWith(`.${d}`))) return p;
  }
  return null;
}

/**
 * Types de cible compatibles avec ce lien, d'après la forme du chemin (prudent) :
 * [] = forme non reconnue pour ce réseau ; null = réseau sans règle → aucune déduction.
 */
function kindsOf(p, u) {
  const host = u.hostname.toLowerCase().replace(/^www\.|^m\./, '');
  const s = u.pathname.split('/').filter(Boolean).map((x) => x.toLowerCase());
  const one = s.length === 1;
  switch (p) {
    case 'instagram':
      if (s[0] === 'p') return ['url_post'];
      if (['reel', 'reels', 'tv'].includes(s[0])) return ['url_video', 'url_reel', 'url_post'];
      if (s[0] === 'stories' && s[1]) return ['username'];
      return one ? ['username', 'url_profile'] : [];
    case 'tiktok':
      if (s.includes('video') || /^v[mt]\./.test(host)) return ['url_video', 'url_post'];
      return one && s[0].startsWith('@') ? ['username', 'url_profile'] : [];
    case 'youtube':
      if (host === 'youtu.be' || ['watch', 'shorts', 'live'].includes(s[0])) return ['url_video'];
      if ((s[0] || '').startsWith('@') || ['channel', 'c', 'user'].includes(s[0])) return ['url_channel'];
      return [];
    case 'x':
      if (s[1] === 'status') return ['url_post'];
      return one ? ['username', 'url_profile'] : [];
    case 'facebook':
      if (host === 'fb.watch' || /\/(posts|videos|reel|photo|permalink|watch)\b|story\.php/.test(u.pathname)) return ['url_post', 'url_video'];
      return one || s[0] === 'profile.php' ? ['url_profile'] : [];
    case 'twitch':
      if (s[0] === 'videos' || s[1] === 'videos' || s[1] === 'clip' || host.startsWith('clips.')) return ['url_video'];
      return one ? ['url_channel', 'username'] : [];
    case 'spotify':
      if (s[0] === 'track') return ['url_track'];
      if (s[0] === 'playlist') return ['url_playlist'];
      if (['artist', 'user'].includes(s[0])) return ['url_profile'];
      return [];
    case 'linkedin':
      if (['posts', 'feed'].includes(s[0])) return ['url_post'];
      return ['in', 'company'].includes(s[0]) ? ['url_profile'] : [];
    case 'snapchat':
      if (s[0] === 'spotlight') return ['url_video'];
      return s[0] === 'add' && s[1] ? ['username'] : [];
    case 'discord':
      return ['url_invite'];
    default:
      return null;
  }
}

/** Analyse de la saisie : { kind: 'url'|'handle'|'text'|'invalid'|'long', ... } */
function parse(raw) {
  const v = clean(raw);
  if (!v) return { kind: 'text' };
  if (v.length > MAX) return { kind: 'long' };
  if (/^(javascript|data|vbscript|file):/i.test(v)) return { kind: 'invalid' };
  const looksUrl = /^https?:\/\//i.test(v) || /^[a-z0-9-]+(\.[a-z0-9-]+)+\/\S*/i.test(v);
  if (looksUrl) {
    let u;
    try { u = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`); } catch { return { kind: 'invalid' }; }
    if (!/^https?:$/.test(u.protocol) || /\s/.test(v) || u.username || u.password) return { kind: 'invalid' };
    const platform = platformOf(u.hostname);
    if (!platform) return { kind: 'unknown' };
    u.protocol = 'https:';
    return { kind: 'url', platform, kinds: kindsOf(platform, u), target: u.href };
  }
  const m = /^@([\p{L}\p{N}._-]{1,64})$/u.exec(v);
  if (m) return { kind: 'handle', target: `@${m[1]}` };
  return { kind: 'text' };
}

const services = () => (Array.isArray(data?.services) ? data.services : []);
const status = box?.querySelector('[data-fb-tsearch-status]');
const list = box?.querySelector('[data-fb-tsearch-list]');
const nets = box?.querySelector('[data-fb-tsearch-nets]');

function reset() {
  box.hidden = false;
  list.replaceChildren();
  nets.replaceChildren();
  nets.hidden = true;
  status.textContent = '';
}

function say(text) {
  reset();
  status.textContent = text;
}

/** Cartes de prestations (liens internes uniquement, cible encodée dans ?fb_target=). */
function show(items, target) {
  list.replaceChildren();
  for (const it of items) {
    if (typeof it.url !== 'string' || !it.url.startsWith('/') || it.url.startsWith('//')) continue;
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.className = 'fb-tsearch__item';
    a.href = `${it.url}?fb_target=${encodeURIComponent(target)}`;
    const title = document.createElement('strong');
    title.textContent = it.title;
    const meta = document.createElement('span');
    meta.textContent = `${netName(it.platform)} · ${S('from')} ${it.price}`;
    const cta = document.createElement('span');
    cta.className = 'fb-tsearch__cta';
    cta.textContent = S('open');
    a.append(title, meta, cta);
    li.append(a);
    list.append(li);
  }
}

function forUrl(r) {
  const all = services().filter((s) => s.platform === r.platform);
  const items = r.kinds === null ? all : all.filter((s) => r.kinds.includes(s.target));
  if (!items.length) return say(S('noMatch', { network: netName(r.platform) }));
  reset();
  status.textContent = `${S('results')} — ${netName(r.platform)}. ${S('note')}`;
  show(items, r.target);
}

function forHandle(r) {
  reset();
  status.textContent = S('pickNetwork', { target: r.target });
  const platforms = [...new Set(services().map((s) => s.platform))];
  nets.hidden = false;
  for (const p of platforms) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'fb-chip';
    b.textContent = netName(p);
    b.setAttribute('aria-pressed', 'false');
    b.addEventListener('click', () => {
      nets.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      const items = services().filter((s) => s.platform === p && s.target === 'username');
      if (!items.length) {
        list.replaceChildren();
        status.textContent = S('noUsername', { network: netName(p) });
        return;
      }
      status.textContent = `${S('results')} — ${netName(p)}. ${S('note')}`;
      show(items, r.target);
    });
    nets.append(b);
  }
  nets.querySelector('button')?.focus();
}

if (box && form && data) {
  const input = form.querySelector('input[name="q"]');
  input?.setAttribute('maxlength', String(MAX));
  form.addEventListener('submit', (e) => {
    const r = parse(input?.value);
    if (r.kind === 'text') {
      // recherche Shopify classique : l'ancien message disparaît immédiatement
      box.hidden = true;
      status.textContent = '';
      list.replaceChildren();
      nets.replaceChildren();
      return;
    }
    e.preventDefault();
    if (r.kind === 'long') return say(S('tooLong'));
    if (r.kind === 'invalid') return say(S('invalid'));
    if (r.kind === 'unknown') return say(S('unknownLink'));
    if (r.kind === 'url') return forUrl(r);
    if (r.kind === 'handle') return forHandle(r);
  });
}
